import { createElement } from 'react';
import type { BlockComponent } from '@akademi/site-builder-runtime';
import { TradeSkin } from './withTradeSkin';
import { SKINNED_BLOCKS } from './skinnedBlocks';

/**
 * The theme's overrides for the platform's functional blocks: every entry in
 * {@link SKINNED_BLOCKS} mapped to a wrapper that renders {@link TradeSkin} with
 * that block's type and width baked in.
 *
 * <p><strong>No `'use client'` here, deliberately.</strong> These wrappers use no
 * hooks and no browser API — each only renders a client component with fixed
 * props, which is the one thing a server module may do across the boundary. That
 * is what lets `../index.ts` build the theme bundle at module scope on either
 * side: the server route that imports the extensions module for its API prefixes
 * evaluates this file without ever calling into client code, and the browser gets
 * the same wrappers in its own bundle.
 *
 * <p>It is a `const`, not a function, so there is no call for a server import to
 * get wrong.
 *
 * <p>`createElement` rather than JSX: this file produces components instead of
 * being one, and the explicit call keeps the passthrough obvious — the block's
 * own props go through untouched as `blockProps`, so nothing the platform block
 * expects is renamed or dropped on the way in.
 */
export const tradeSkinOverrides: Record<string, BlockComponent> = Object.fromEntries(
  Object.entries(SKINNED_BLOCKS).map(([type, width]) => {
    function TradeSkinned(props: Record<string, unknown>) {
      return createElement(TradeSkin, { skinType: type, skinWidth: width, blockProps: props });
    }
    TradeSkinned.displayName = `TradeSkin(${type})`;
    return [type, TradeSkinned as BlockComponent];
  }),
);
